// apps/smart-shikka-web/src/pages/chat/chatApi.ts
// Chat page helpers — CSRF, headers, conversation fetch/create, and the
// server-message → ProductChatMessage mapper. Extracted verbatim from
// pages/Chat.tsx (CORE-14 modularization); artifact cards are mapped through
// mapDurableArtifacts (CORE-11 correction — bounded validation, no invented
// fields).
import type { ProductChatMessage } from "@smart-shikka/react";
import { mapDurableArtifacts } from "../chatMessageArtifacts.js";

export const BASE = (): string =>
  ((import.meta as any).env?.VITE_API_BASE_URL as string | undefined) ?? window.location.origin;

export interface Session {
  id: string;
  title: string;
  status?: string | undefined;
  archivedAt?: number | undefined;
  updatedAt?: number | undefined;
}
export interface ChatAttachment { name: string; mimeType: string; base64Data: string; }

export const MAX_ATTACH_BYTES = 2_000_000;
const CSRF_COOKIE_NAME = "smart_shikka_csrf";
export const CSRF_HEADER_NAME = "x-smart-shikka-csrf";

export function createVoiceSessionId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `voice-${crypto.randomUUID()}`;
  }
  return `voice-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export function readCsrfCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  for (const part of document.cookie.split(";")) {
    const [name, ...rest] = part.trim().split("=");
    if (name === CSRF_COOKIE_NAME) return decodeURIComponent(rest.join("="));
  }
  return undefined;
}

export function buildHeaders(token: string | null): HeadersInit {
  const csrf = readCsrfCookie();
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(csrf ? { [CSRF_HEADER_NAME]: csrf } : {}),
  };
}

export interface ConversationListParams {
  q?: string | undefined;
  status?: "active" | "archived" | "deleted" | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export async function fetchConversationList(
  headers: HeadersInit,
  anonymousTitle: string,
  params?: ConversationListParams,
): Promise<Session[]> {
  const qp = new URLSearchParams();
  if (params?.q && params.q.trim() !== "") qp.set("q", params.q.trim());
  if (params?.status) qp.set("status", params.status);
  if (typeof params?.limit === "number") qp.set("limit", String(params.limit));
  if (typeof params?.offset === "number" && params.offset > 0) qp.set("offset", String(params.offset));
  const qs = qp.toString();
  const r = await fetch(`${BASE()}/api/chat/conversations${qs ? `?${qs}` : ""}`, { headers, credentials: "include" });
  const d = r.ok ? ((await r.json()) as Record<string, unknown>) : null;
  const arr = (d?.conversations ?? d?.data ?? (Array.isArray(d) ? d : [])) as Record<string, unknown>[];
  return arr.map((s) => ({
    id: String(s.conversationId ?? s.id),
    title: String(s.title || anonymousTitle),
    status: typeof s.status === "string" ? s.status : undefined,
    archivedAt: typeof s.archivedAt === "number" ? s.archivedAt : undefined,
    updatedAt: typeof s.updatedAt === "number" ? s.updatedAt : undefined,
  }));
}

export async function updateConversationMeta(
  headers: HeadersInit,
  conversationId: string,
  patch: { title?: string; status?: "active" | "archived" },
): Promise<boolean> {
  const r = await fetch(`${BASE()}/api/chat/conversations/${encodeURIComponent(conversationId)}/update`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  return r.ok;
}

export async function deleteConversation(headers: HeadersInit, conversationId: string): Promise<boolean> {
  const r = await fetch(`${BASE()}/api/chat/conversations/${encodeURIComponent(conversationId)}/delete`, {
    method: "POST",
    headers,
    credentials: "include",
    body: "null",
  });
  return r.ok;
}

export async function createConversationSession(
  headers: HeadersInit,
  title: string,
): Promise<Session | null> {
  const r = await fetch(`${BASE()}/api/chat/conversations`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify({ title }),
  }).catch(() => null);
  if (!r?.ok) return null;
  const d = (await r.json().catch(() => null)) as Record<string, unknown> | null;
  if (!d) return null;
  const conv = (d.conversation ?? d.data) as Record<string, unknown> | undefined;
  return { id: String(conv?.conversationId ?? conv?.id ?? d.id), title };
}

export function mapServerMessages(d: Record<string, unknown> | null): ProductChatMessage[] {
  const arr = (d?.messages ?? d?.data ?? (Array.isArray(d) ? d : [])) as Record<string, unknown>[];
  let lastUserPrompt: string | undefined;
  return arr.map((m) => {
    const role = String(m.role ?? "user") === "assistant" ? "assistant" : "user";
    const priorPrompt = role === "assistant" ? lastUserPrompt : undefined;
    if (role === "user") lastUserPrompt = String(m.content ?? "");
    const ev =
      typeof m.turnEvidence === "object" && m.turnEvidence !== null
        ? (m.turnEvidence as Record<string, unknown>)
        : undefined;
    const s = (v: unknown) => (typeof v === "string" ? v : undefined);
    const citations = Array.isArray(m.citations)
      ? (m.citations as Record<string, unknown>[]).map((c) => ({
          label: String(c.label ?? c.source ?? c.appId ?? "source"),
          appId: s(c.appId),
          sourceKind: s(c.kind ?? c.sourceKind),
        }))
      : undefined;
    const toolActions = Array.isArray(m.toolEvents)
      ? (m.toolEvents as Record<string, unknown>[]).map((e) => ({
          name: String(e.name ?? e.toolName ?? "tool"),
          status: String(e.status ?? "recorded"),
          detail: s(e.detail),
        }))
      : undefined;
    const approvalId = s(ev?.["approvalId"]);
    const artifacts = mapDurableArtifacts(ev);
    const tm =
      typeof m.tokenMetadata === "object" && m.tokenMetadata !== null
        ? (m.tokenMetadata as Record<string, unknown>)
        : undefined;
    const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
    const trace =
      (ev && (s(ev["logicalProfile"]) || s(ev["runId"]) || approvalId || s(ev["errorCode"]))) || tm
        ? {
            modelProfile: s(ev?.["logicalProfile"]),
            runId: s(ev?.["runId"]),
            approvalId,
            errorCode: s(ev?.["errorCode"]),
            turnState: s(ev?.["state"]),
            delegatedTo: s(ev?.["delegatedTo"]),
            contextMode: s(tm?.["contextMode"]),
            retrievalTokens: n(tm?.["retrievalTokens"]),
            conversationTokens: n(tm?.["conversationTokens"]),
            workingTokens: n(tm?.["workingTokens"]),
            remainingBudget: n(tm?.["remainingBudget"]),
            retrievedChunkCount: n(tm?.["retrievedChunkCount"]),
          }
        : undefined;
    return {
      id: String(m.messageId ?? m.id),
      role,
      content: String(m.content ?? ""),
      retryPrompt: priorPrompt,
      citations,
      toolActions,
      approvals: approvalId
        ? [{ approvalId, status: String(m.approvalStatus ?? "pending"), actionDigest: s(ev?.["approvalActionDigest"]) }]
        : undefined,
      trace,
      artifacts,
      errorCode: s(m.errorCode) ?? s(ev?.["errorCode"]),
      generationState:
        m.generationState === "streaming" ||
        m.generationState === "complete" ||
        m.generationState === "cancelled" ||
        m.generationState === "error"
          ? m.generationState
          : undefined,
    };
  });
}

export async function fetchConversationMessages(
  headers: HeadersInit,
  conversationId: string,
): Promise<ProductChatMessage[]> {
  const r = await fetch(`${BASE()}/api/chat/conversations/${conversationId}/messages`, {
    headers,
    credentials: "include",
  });
  const d = r.ok ? ((await r.json().catch(() => null)) as Record<string, unknown> | null) : null;
  return mapServerMessages(d);
}

export async function postAsk(
  headers: HeadersInit,
  body: Record<string, unknown>,
): Promise<Record<string, unknown> | null> {
  const r = await fetch(`${BASE()}/api/ask`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
  }).catch(() => null);
  if (!r) return null;
  return (await r.json().catch(() => null)) as Record<string, unknown> | null;
}
